import InternalOnlyBadge, { InlineInternalOnlyBadge } from '@orn/shared-web/components/InternalOnlyBadge';
import { cx } from '@orn/shared-web/utils/cx';
import { Link as RouterLink } from 'react-router-dom';

import { PATHS } from '../../paths';
import { BRAND_COLOR } from '../logos/mantleLogo';
import MantleMarkPrimary from '../logos/MantleMarkPrimary';

const MARK_SIZE_FULL_PX = 16;
const MARK_SIZE_COMPACT_PX = 18;

interface IMantleAssistantNavButtonProps {
  /**
   * `full` (default) renders the wide footer pill with the Mantle mark
   * plus "Mantle Assistant" label, used inside the desktop sidebar and
   * the mobile drawer. `compact` renders the 36px square icon-only
   * variant used inside the 64px tablet icon rail.
   */
  variant?: 'full' | 'compact';
}

/**
 * Footer entry-point for the Mantle Assistant route (`/assistant`).
 * Visually distinct from the regular nav list — a mist-colored pill
 * (`bg-utility-mist-400`) with the Mantle mark and label per Figma node
 * `4319:92664` (full) and `4531:24287` "Mantle AI Button" (compact). The
 * caller (`FullSidebarContent` / `IconRailSidebar` / `MobileNavDrawer`)
 * is responsible for gating visibility behind `feat-assistant`; this
 * component renders unconditionally when mounted.
 *
 * The internal-only annotation follows the same split as the
 * "Bills & Subscriptions" nav item (see `MainSidebar.tsx`):
 * - **Full pill** uses {@link InlineInternalOnlyBadge} after the label,
 *   since there's room for a trailing pip next to "Mantle Assistant".
 * - **Compact icon rail** overlays {@link InternalOnlyBadge} on the
 *   mark — at 36px there's no label to anchor the inline pip to.
 */
const MantleAssistantNavButton = ({ variant = 'full' }: IMantleAssistantNavButtonProps) => {
  if (variant === 'compact') {
    return (
      <RouterLink
        to={PATHS.ASSISTANT}
        aria-label="Mantle Assistant"
        className={cx(
          'bg-utility-mist-400 border-utility-mist-500 outline-focus-ring',
          'relative flex size-9 shrink-0 items-center justify-center rounded-lg border',
          'transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2'
        )}
      >
        <MantleMarkPrimary size={MARK_SIZE_COMPACT_PX} color={BRAND_COLOR} aria-hidden="true" />
        {/* Badge anchored to the button (not the icon) so it overhangs the
            outer top-right corner — the 36px button is small enough that
            an interior badge would crowd the mark. */}
        <InternalOnlyBadge />
      </RouterLink>
    );
  }

  return (
    <RouterLink
      to={PATHS.ASSISTANT}
      className={cx(
        'bg-utility-mist-400 border-utility-mist-500 outline-focus-ring',
        'flex w-full items-center justify-center gap-2 rounded-md border px-4 py-3',
        'transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2'
      )}
    >
      <MantleMarkPrimary size={MARK_SIZE_FULL_PX} color={BRAND_COLOR} aria-hidden="true" />
      {/* Pip is nested INSIDE the label span (not a sibling) so the parent
          flex's `gap-2` only separates the icon from the label; the
          label-to-pip distance is governed by the badge's own margin. */}
      <span className="text-md font-semibold" style={{ color: BRAND_COLOR }}>
        Mantle Assistant
        <InlineInternalOnlyBadge className="ml-1" />
      </span>
    </RouterLink>
  );
};

export default MantleAssistantNavButton;
